import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { QueryIntent, ShopProduct } from '../../../../../src/types';
import { LanguageService } from '../../services/language.service';
import { ShopSearchService } from '../../services/shop-search.service';
import { formatPrice } from '../../utils/format';

/** Wait this long after the last keystroke before searching. */
export const SEARCH_DEBOUNCE_MS = 200;
/** And this long before letting the AI read the query, as the widget does: never while typing. */
export const AI_DEBOUNCE_MS = 800;

/**
 * A shop's search, as its customers will meet it: a box and the products it
 * finds while they type. The home page's demo and a shop's dashboard both show it.
 * With `examples`, it offers a few searches to try, made from the shop's own
 * catalogue (the word, in Latin, with a typo, with a price), one click each.
 * With `similar`, it also shows the products similar to a result (the top one,
 * or the one whose "Similar" was pressed), as a product page would show them.
 */
@Component({
   selector: 'app-shop-search',
   standalone: true,
   imports: [FormsModule, TranslateModule],
   template: `
      <div class="text-left">
         <input id="shop-search" type="search" [(ngModel)]="query" (ngModelChange)="typed()" autocomplete="off"
            [placeholder]="'pages.index-page.try.searchPlaceholder' | translate"
            [attr.aria-label]="'pages.index-page.try.searchPlaceholder' | translate"
            class="w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
         @if (exampleQueries.length) {
            <p class="flex flex-wrap items-center gap-2 mt-2 text-sm" id="shop-search-examples">
               <span class="text-slate-500 dark:text-slate-400">{{ 'pages.index-page.try.examples' | translate }}</span>
               @for (example of exampleQueries; track example) {
                  <button type="button" (click)="tryExample(example)"
                     class="rounded-full border px-3 py-1 transition"
                     [class]="query === example ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-slate-300 dark:border-slate-600 hover:border-cyan-600 hover:text-cyan-700 dark:hover:text-cyan-300'">{{ example }}</button>
               }
            </p>
         }
         @if (searched) {
            @if (understood) {
               <p class="text-sm text-cyan-700 dark:text-cyan-300 mt-2" id="shop-search-understood">
                  <i class="mdi mdi-creation" aria-hidden="true"></i>
                  {{ 'pages.index-page.try.understood' | translate: { what: understoodText } }}
               </p>
            }
            <p class="text-sm text-slate-500 dark:text-slate-400 mt-2" aria-live="polite">
               @if (total > 0) {
                  {{ 'pages.index-page.try.total' | translate: { count: total } }}
                  @if (maxPrice && !understood) {
                     · {{ 'pages.index-page.try.under' | translate: { price: maxPrice } }}
                  }
               } @else {
                  {{ 'pages.index-page.try.noResults' | translate }}
               }
            </p>
            @if (total === 0 && suggestions.length) {
               <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-3" id="shop-search-popular">
                  {{ 'pages.index-page.try.popular' | translate }}
               </p>
            }
            <ul class="mt-2 divide-y divide-slate-200 dark:divide-slate-700" id="shop-search-results">
               @for (product of (total === 0 ? suggestions : results); track product.id) {
                  <li class="flex items-center gap-2">
                     <a [href]="product.url" target="_blank" rel="noopener" class="flex-1 min-w-0 flex items-center gap-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg px-1">
                        @if (product.image) {
                           <img [src]="product.image" alt="" loading="lazy" class="size-12 rounded-lg object-contain bg-white shrink-0" />
                        } @else {
                           <span class="size-12 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0"></span>
                        }
                        <span class="min-w-0 flex-1">
                           <span class="block truncate">{{ product.title }}</span>
                           @if (product.availability === 'out_of_stock') {
                              <span class="text-xs text-amber-600">{{ 'pages.index-page.outOfStock' | translate }}</span>
                           }
                        </span>
                        @if (product.price > 0) {
                           <span class="font-semibold whitespace-nowrap">{{ price(product) }}</span>
                        }
                     </a>
                     @if (similar) {
                        <button type="button" (click)="showSimilar(product)" [attr.aria-pressed]="similarOf?.id === product.id"
                           class="shrink-0 text-xs font-semibold rounded-lg px-2 py-1 border transition"
                           [class]="similarOf?.id === product.id ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-cyan-600'">
                           {{ 'pages.index-page.try.similarButton' | translate }}
                        </button>
                     }
                  </li>
               }
            </ul>
            @if (similar && similarOf && similarProducts.length) {
               <div class="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4" id="shop-search-similar">
                  <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                     {{ 'pages.index-page.try.similar' | translate: { title: similarOf.title } }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ 'pages.index-page.try.similarHint' | translate }}</p>
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                     @for (product of similarProducts; track product.id) {
                        <a [href]="product.url" target="_blank" rel="noopener" class="flex flex-col gap-1 min-w-0 rounded-lg p-1 hover:bg-slate-50 dark:hover:bg-slate-800">
                           @if (product.image) {
                              <img [src]="product.image" alt="" loading="lazy" class="w-full aspect-square rounded-lg object-contain bg-white" />
                           } @else {
                              <span class="w-full aspect-square rounded-lg bg-slate-100 dark:bg-slate-800"></span>
                           }
                           <span class="text-sm line-clamp-2 [overflow-wrap:break-word]">{{ product.title }}</span>
                           @if (product.price > 0) {
                              <span class="text-sm font-semibold">{{ price(product) }}</span>
                           }
                        </a>
                     }
                  </div>
               </div>
            }
         }
      </div>
   `,
})
export class ShopSearchComponent implements OnInit, OnDestroy {
   @Input({ required: true }) shopId!: string;
   /** Show the products similar to a result, as the shop's product pages would (see widget.js). */
   @Input() similar = false;
   /** Offer a few searches to try, made from the shop's catalogue. */
   @Input() examples = false;
   exampleQueries: string[] = [];
   /** The result whose similar products are shown, and those products. */
   similarOf?: ShopProduct;
   similarProducts: ShopProduct[] = [];
   private similarTicket = 0;
   query = '';
   results: ShopProduct[] = [];
   total = 0;
   /** When nothing is found: what the shop's customers chose most (plans with recommendations). */
   suggestions: ShopProduct[] = [];
   maxPrice?: number;
   /** How the AI read the query, when it did. */
   understood?: QueryIntent;
   searched = false;
   private timer?: ReturnType<typeof setTimeout>;
   private aiTimer?: ReturnType<typeof setTimeout>;

   constructor(private searchService: ShopSearchService, private language: LanguageService) {}

   ngOnInit() {
      if (!this.examples) return;
      this.searchService.examples(this.shopId)
         .then((found) => (this.exampleQueries = found))
         .catch(() => undefined);
   }

   /** Search an example at once, and let the AI read it after the usual pause. */
   tryExample(example: string) {
      this.query = example;
      clearTimeout(this.timer);
      clearTimeout(this.aiTimer);
      this.run();
      this.aiTimer = setTimeout(() => this.run(true), AI_DEBOUNCE_MS);
   }

   typed() {
      clearTimeout(this.timer);
      clearTimeout(this.aiTimer);
      this.timer = setTimeout(() => this.run(), SEARCH_DEBOUNCE_MS);
      this.aiTimer = setTimeout(() => this.run(true), AI_DEBOUNCE_MS);
   }

   async run(ai = false) {
      const query = this.query.trim();
      if (!query) {
         this.searched = false;
         this.results = [];
         this.understood = undefined;
         this.clearSimilar();
         return;
      }
      try {
         const response = await this.searchService.search(this.shopId, query, 12, ai);
         // Cancelled by a newer search, or the box changed while the AI read it.
         if (!response || this.query.trim() !== query) return;
         this.results = response.results;
         this.total = response.total;
         this.suggestions = response.suggestions ?? [];
         this.maxPrice = response.maxPrice;
         this.understood = response.understood;
         this.followResults();
      } catch {
         this.results = [];
         this.total = 0;
         this.suggestions = [];
         this.understood = undefined;
         this.clearSimilar();
      }
      this.searched = true;
   }

   /** Keep showing the chosen result's similar products while it is still a result; else the top result's. */
   private followResults() {
      if (!this.similar) return;
      const shown = (this.total === 0 ? this.suggestions : this.results);
      if (this.similarOf && shown.some((p) => p.id === this.similarOf?.id)) return;
      if (shown.length) this.showSimilar(shown[0]);
      else this.clearSimilar();
   }

   async showSimilar(product: ShopProduct) {
      this.similarOf = product;
      const ticket = ++this.similarTicket;
      let found: ShopProduct[] = [];
      try {
         found = await this.searchService.similar(this.shopId, product.id, 4);
      } catch {
         found = [];
      }
      // A newer choice or search wins.
      if (ticket === this.similarTicket) this.similarProducts = found;
   }

   private clearSimilar() {
      this.similarTicket++;
      this.similarOf = undefined;
      this.similarProducts = [];
   }

   /** "самобръсначки · ≥ 5 ножчета · ≤ 25,56 €": the AI's reading, in a line. */
   get understoodText(): string {
      const intent = this.understood;
      if (!intent) return '';
      const currency = this.results[0]?.currency ?? 'EUR';
      const parts = [intent.keywords[0]];
      if (intent.brand) parts.push(intent.brand);
      if (intent.minPrice) parts.push(`≥ ${formatPrice(intent.minPrice, currency, this.language.current)}`);
      if (intent.maxPrice) parts.push(`≤ ${formatPrice(intent.maxPrice, currency, this.language.current)}`);
      for (const spec of intent.specs ?? []) {
         if (spec.min !== undefined && spec.min === spec.max) parts.push(`${spec.min} ${spec.unit}`);
         else {
            if (spec.min !== undefined) parts.push(`≥ ${spec.min} ${spec.unit}`);
            if (spec.max !== undefined) parts.push(`≤ ${spec.max} ${spec.unit}`);
         }
      }
      return parts.join(' · ');
   }

   price(product: ShopProduct): string {
      return formatPrice(product.price, product.currency, this.language.current);
   }

   ngOnDestroy() {
      clearTimeout(this.timer);
      clearTimeout(this.aiTimer);
   }
}
